/**
 * MobileBatteryPanel Component for LOOPINO AI Platform
 * Detailed AI battery management and history screen
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './MobileBatteryPanel.css';
import { API_CONFIG } from '../../../config/api';
import Iridescence from './Iridescence';
import useLanguage from '../../../hooks/useLanguage';
import { Skeleton } from '../../effects';
import { apiFetch, parseResponseBody } from '../../../utils/http';

// Import icons
import iconAi from '../../../assets/images/icons/icon-ai.svg';
import iconCalendar from '../../../assets/images/icons/icon-calendar.svg';

const toNumber = (value, fallback = 0) => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const WEEK_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000;

const computeWeeklyCycle = ({
  currentWeekStart,
  currentWeekEnd,
  nextRefreshAt,
  lastRefreshAt
} = {}) => {
  const now = new Date();

  const parsedLastRefresh = parseAPITimestamp(lastRefreshAt) || parseAPITimestamp(currentWeekStart);
  const parsedWeekStart = parseAPITimestamp(currentWeekStart)
    || (parsedLastRefresh ? new Date(parsedLastRefresh) : new Date(now.getTime() - WEEK_INTERVAL_MS));

  const parsedWeekEnd = parseAPITimestamp(currentWeekEnd)
    || (parsedWeekStart ? new Date(parsedWeekStart.getTime() + WEEK_INTERVAL_MS) : new Date(now.getTime() + WEEK_INTERVAL_MS));

  const parsedNextRefresh = parseAPITimestamp(nextRefreshAt)
    || (parsedWeekEnd ? new Date(parsedWeekEnd) : new Date(parsedWeekStart.getTime() + WEEK_INTERVAL_MS));

  const lastRefresh = parsedLastRefresh && !Number.isNaN(parsedLastRefresh.getTime())
    ? parsedLastRefresh
    : new Date(parsedWeekStart.getTime());

  let nextRefresh = parsedNextRefresh && !Number.isNaN(parsedNextRefresh.getTime())
    ? parsedNextRefresh
    : new Date(parsedWeekEnd.getTime());

  if (nextRefresh <= now) {
    nextRefresh = new Date(nextRefresh.getTime() + WEEK_INTERVAL_MS);
  }

  return {
    lastRefresh,
    nextRefresh,
    weekStart: parsedWeekStart,
    weekEnd: parsedWeekEnd
  };
};

const parseAPITimestamp = (value) => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'number') {
    const asMilliseconds = value > 1e12 ? value : value * 1000;
    const date = new Date(asMilliseconds);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    if (/^\d+$/.test(trimmed)) {
      return parseAPITimestamp(Number(trimmed));
    }

    const hasTimezone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(trimmed);
    const normalized = hasTimezone
      ? trimmed
      : `${trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T')}Z`;

    const date = new Date(normalized);
    if (!Number.isNaN(date.getTime())) {
      return date;
    }

    const fallback = new Date(trimmed.replace(' ', 'T'));
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }

  if (typeof value.toString === 'function') {
    return parseAPITimestamp(value.toString());
  }

  return null;
};

const pickTimestampSource = (entry = {}) => {
  const candidates = [
    entry.timestamp_local,
    entry.timestamp,
    entry.performed_at,
    entry.created_at,
    entry.completed_at,
    entry.date,
    entry.datetime,
    entry.ts,
    entry.time
  ];

  return candidates.find((candidate) => {
    if (candidate === null || candidate === undefined) {
      return false;
    }

    if (candidate instanceof Date) {
      return !Number.isNaN(candidate.getTime());
    }

    if (typeof candidate === 'number') {
      return Number.isFinite(candidate);
    }

    if (typeof candidate === 'string') {
      const trimmed = candidate.trim();
      if (!trimmed) {
        return false;
      }

      const normalized = trimmed.toLowerCase();
      if (
        normalized === '--'
        || normalized === '—'
        || normalized === 'n/a'
        || normalized === 'na'
        || normalized === 'nd'
      ) {
        return false;
      }

      return true;
    }

    return true;
  });
};

const resolveTimestamp = (entry = {}) => {
  if (!entry || typeof entry !== 'object') {
    return null;
  }

  const details = entry.operation_details || entry.operationDetails || {};

  const candidates = [
    pickTimestampSource(entry),
    entry.timestamp,
    entry.timestamp_local,
    entry.timestamp_utc,
    entry.timestampUTC,
    entry.timestampLocal,
    entry.performed_at,
    entry.performedAt,
    entry.completed_at,
    entry.completedAt,
    entry.executed_at,
    entry.executedAt,
    entry.created_at,
    entry.createdAt,
    entry.updated_at,
    entry.updatedAt,
    entry.occurred_at,
    entry.occurredAt,
    entry.happened_at,
    entry.happenedAt,
    entry.recorded_at,
    entry.recordedAt,
    entry.logged_at,
    entry.loggedAt,
    entry.time,
    entry.time_at,
    entry.timeAt,
    entry.ts,
    entry.datetime,
    entry.date,
    entry.time_ms,
    entry.timeMs,
    pickTimestampSource(details),
    details.timestamp,
    details.timestamp_local,
    details.timestamp_utc,
    details.timestampUTC,
    details.timestampLocal,
    details.performed_at,
    details.performedAt,
    details.completed_at,
    details.completedAt,
    details.executed_at,
    details.executedAt,
    details.created_at,
    details.createdAt,
    details.updated_at,
    details.updatedAt,
    details.occurred_at,
    details.occurredAt,
    details.happened_at,
    details.happenedAt,
    details.recorded_at,
    details.recordedAt,
    details.logged_at,
    details.loggedAt,
    details.time,
    details.time_at,
    details.timeAt,
    details.ts,
    details.datetime,
    details.date,
    details.time_ms,
    details.timeMs
  ].filter((candidate) => candidate !== undefined && candidate !== null);

  for (const candidate of candidates) {
    const parsed = parseAPITimestamp(candidate);
    if (parsed) {
      return parsed;
    }
  }

  return null;
};

const extractRelativeTimeLabels = (entry = {}) => {
  if (!entry || typeof entry !== 'object') {
    return [];
  }

  const details = entry.operation_details || entry.operationDetails || {};
  const keys = [
    'relative_time',
    'relativeTime',
    'timeAgo',
    'timeAgoLabel',
    'timestamp_label',
    'timestampLabel',
    'display_time',
    'displayTime',
    'display_timestamp',
    'displayTimestamp',
    'time_label',
    'timeLabel',
    'elapsed',
    'elapsed_time',
    'elapsedTime'
  ];

  const collectValues = (source) => keys
    .map((key) => source[key])
    .filter((value) => typeof value === 'string');

  return [...collectValues(entry), ...collectValues(details)];
};

const extractOperationTokens = (value) => {
  if (!value) return [];

  const normalized = value
    .toString()
    .trim()
    .toLowerCase();

  if (!normalized) {
    return [];
  }

  return normalized
    .split(/[./]/)
    .reduce((acc, segment) => {
      const parts = segment
        .split(/[\s_-]+/)
        .map((token) => token.trim())
        .filter(Boolean);
      return acc.concat(parts);
    }, []);
};

const sanitizeOperationTokens = (tokens) => {
  if (!Array.isArray(tokens)) {
    return [];
  }

  const blacklist = new Set([
    'aibattery',
    'operation',
    'operations',
    'operationtoken',
    'operationtokens',
    'op',
    'token',
    'tokens'
  ]);

  const sanitized = tokens.filter((token) => !blacklist.has(token));
  return Array.from(new Set(sanitized));
};

const MobileBatteryPanel = ({ onBack, onOpenPlans }) => {
  const initialCycle = computeWeeklyCycle({
    currentWeekStart: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
    currentWeekEnd: new Date(Date.now() + 4 * 24 * 60 * 60 * 1000),
    lastRefreshAt: new Date(Date.now() - 24 * 60 * 60 * 1000)
  });

  const [batteryData, setBatteryData] = useState({
    percentage: 60,
    capacityUnits: 1.5,
    baseCapacityUnits: 1.5,
    remainingUnits: 0.9,
    consumedUnits: 0.6,
    isUpgraded: false,
    lastRecharge: initialCycle.lastRefresh,
    nextRecharge: initialCycle.nextRefresh,
    status: 'active',
    lastUpdated: new Date(),
    weekStart: initialCycle.weekStart,
    weekEnd: initialCycle.weekEnd,
    subscription: null,
    weekKey: null
  });

  const [usageOverview, setUsageOverview] = useState({
    operationCount: null,
    byType: {}
  });

  const [lastUsage, setLastUsage] = useState(null);
  const [batteryHistory, setBatteryHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);

  const { t, language } = useLanguage();

  const renderHistorySkeleton = (count = 3) => (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <div key={`history-skeleton-${index}`} className="mobile-history-item">
          <div className="mobile-history-icon">
            <Skeleton variant="circle" width={36} height={36} shimmer />
          </div>
          <div className="mobile-history-content">
            <div className="mobile-history-header">
              <Skeleton width="50%" height={14} shimmer />
            </div>
            <div className="mobile-history-meta">
              <Skeleton width="70%" height={12} shimmer style={{ marginBottom: 4 }} />
              <Skeleton width="40%" height={12} shimmer />
            </div>
          </div>
        </div>
      ))}
    </>
  );

  const renderLoadingSkeleton = () => {
    const statPlaceholders = Array.from({ length: 4 });
    const chipPlaceholders = Array.from({ length: 3 });

    return (
      <div className="mobile-battery-panel">
        <div className="mobile-battery-content">
          <div className="mobile-battery-header">
            <Skeleton width="60%" height={28} shimmer />
            <Skeleton width="80%" height={16} shimmer style={{ marginTop: 8 }} />
          </div>

          <div className="mobile-battery-display-section">
            <div className="mobile-battery-bar">
              <Skeleton variant="rounded" width="100%" height={140} shimmer />
            </div>
          </div>

          <div className="mobile-battery-stats">
            {statPlaceholders.map((_, index) => (
              <div key={`stat-skeleton-${index}`} className="mobile-battery-stat-card">
                <Skeleton width="50%" height={14} shimmer />
                <Skeleton width="70%" height={20} shimmer style={{ margin: '6px 0' }} />
                <Skeleton width="80%" height={12} shimmer />
              </div>
            ))}
          </div>

          <div className="mobile-usage-breakdown">
            <div className="mobile-usage-breakdown-header">
              <Skeleton variant="circle" width={40} height={40} shimmer />
              <div className="mobile-usage-text" style={{ flex: 1, marginLeft: 12 }}>
                <Skeleton width="40%" height={18} shimmer />
                <Skeleton width="60%" height={14} shimmer style={{ marginTop: 6 }} />
              </div>
            </div>
            <div className="mobile-usage-chips">
              {chipPlaceholders.map((_, index) => (
                <div key={`chip-skeleton-${index}`} className="mobile-usage-chip">
                  <Skeleton width="80%" height={14} shimmer />
                </div>
              ))}
            </div>
          </div>

          <div className="mobile-battery-history">
            <Skeleton width="40%" height={18} shimmer style={{ marginBottom: 12 }} />
            <div className="mobile-history-list">
              {renderHistorySkeleton()}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const integerFormatter = useMemo(() => {
    try {
      return new Intl.NumberFormat(language || 'it', { maximumFractionDigits: 0 });
    } catch (error) {
      console.warn('[MobileBatteryPanel] Falling back to default integer formatter', error);
      return new Intl.NumberFormat('it', { maximumFractionDigits: 0 });
    }
  }, [language]);

  const decimalFormatter = useMemo(() => {
    try {
      return new Intl.NumberFormat(language || 'it', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 0
      });
    } catch (error) {
      console.warn('[MobileBatteryPanel] Falling back to default decimal formatter', error);
      return new Intl.NumberFormat('it', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 0
      });
    }
  }, [language]);

  const dateFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(language || 'it', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.warn('[MobileBatteryPanel] Falling back to default date formatter', error);
      return new Intl.DateTimeFormat('it', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    }
  }, [language]);

  const dateTimeFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(language || 'it', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.warn('[MobileBatteryPanel] Falling back to default datetime formatter', error);
      return new Intl.DateTimeFormat('it', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }, [language]);

  const formatCount = useCallback((value) => {
    if (value === null || value === undefined) return '--';
    const numericValue = toNumber(value, NaN);
    if (!Number.isFinite(numericValue)) return '--';
    return integerFormatter.format(numericValue);
  }, [integerFormatter]);

  const formatTokens = useCallback((value) => {
    const numericValue = toNumber(value, NaN);
    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      return null;
    }

    const formatted = formatCount(numericValue);
    if (formatted === '--') {
      return null;
    }

    return t('aiBattery.common.tokens', {
      count: numericValue,
      formatted
    });
  }, [formatCount, t]);

  const formatImages = useCallback((value) => {
    const numericValue = toNumber(value, NaN);
    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      return null;
    }

    const formatted = formatCount(numericValue);
    if (formatted === '--') {
      return null;
    }

    return t('aiBattery.common.images', {
      count: numericValue,
      formatted
    });
  }, [formatCount, t]);

  const formatOperationsLabel = useCallback((value) => {
    const numericValue = toNumber(value, NaN);
    if (!Number.isFinite(numericValue) || numericValue < 0) {
      return '--';
    }

    const formatted = formatCount(numericValue);
    if (formatted === '--') {
      return '--';
    }

    return t('aiBattery.common.operations', {
      count: numericValue,
      formatted
    });
  }, [formatCount, t]);

  const translateOperationToken = useCallback((token) => {
    if (!token) {
      return '';
    }

    const lower = token.toLowerCase();
    if (lower === 'ai') {
      return 'AI';
    }

    const translationKey = `aiBattery.operationTokens.${lower}`;
    const translated = t(translationKey, { defaultValue: '' }).trim();
    if (
      translated
      && translated !== translationKey
      && !translated.startsWith('aiBattery.operationTokens.')
    ) {
      return translated;
    }

    if (lower.length <= 2) {
      return lower.toUpperCase();
    }

    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }, [t]);

  const getActionNameFromType = useCallback((operationType, metadata = {}) => {
    const details = metadata.operation_details || metadata.operationDetails || {};

    const labelCandidate = [
      metadata.operation_label,
      metadata.operation_name,
      metadata.display_name,
      metadata.name,
      metadata.title,
      metadata.label,
      metadata.operation,
      metadata.event,
      metadata.action_label,
      metadata.action_name,
      metadata.description,
      details.display_name,
      details.ui_label,
      details.title,
      details.label,
      details.name,
      details.action_label,
      details.operation,
      details.action,
      details.event
    ].find((candidate) => typeof candidate === 'string' && candidate.trim().length > 0);

    if (labelCandidate) {
      return labelCandidate.trim();
    }

    const normalizedType = (operationType || metadata.operation_type || '').toString().trim().toLowerCase();
    if (!normalizedType) {
      return t('aiBattery.fallbackOperation');
    }

    const rawTokens = extractOperationTokens(normalizedType);
    const tokens = sanitizeOperationTokens(rawTokens);

    const candidateKeys = [
      normalizedType,
      normalizedType.replace(/[./]/g, '_'),
      tokens.join('_'),
      tokens[tokens.length - 1]
    ].filter(Boolean);

    for (const key of candidateKeys) {
      const translationKey = `aiBattery.operations.${key}`;
      const translatedType = t(translationKey, { defaultValue: '' }).trim();
      if (
        translatedType
        && translatedType !== translationKey
        && !translatedType.startsWith('aiBattery.operations.')
      ) {
        return translatedType;
      }
    }

    if (normalizedType === 'weekly_refresh') {
      return t('aiBattery.operations.weeklyRefresh', {
        defaultValue: 'Refill settimanale'
      });
    }

    const translatedTokens = tokens.map((word) => translateOperationToken(word)).filter(Boolean);
    if (translatedTokens.length > 0) {
      return translatedTokens.join(' ');
    }

    const fallbackTokens = rawTokens.map((word) => translateOperationToken(word)).filter(Boolean);
    if (fallbackTokens.length > 0) {
      return fallbackTokens.join(' ');
    }

    return t('aiBattery.fallbackOperation');
  }, [t, translateOperationToken]);

  const formatTimestamp = useCallback((timestamp) => {
    const ts = parseAPITimestamp(timestamp);
    if (!ts) return '--';

    const now = new Date();
    const diff = now - ts;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return t('aiBattery.time.seconds');
    if (minutes < 60) return t('aiBattery.time.minutes', { count: minutes });
    if (hours < 24) return t('aiBattery.time.hours', { count: hours });
    if (days === 1) return t('aiBattery.time.yesterday');
    if (days < 7) return t('aiBattery.time.days', { count: days });

    return dateFormatter.format(ts);
  }, [dateFormatter, t]);

  const formatShortDate = useCallback((timestamp) => {
    const ts = parseAPITimestamp(timestamp);
    if (!ts) return '--';

    return dateTimeFormatter.format(ts);
  }, [dateTimeFormatter]);

  const computeHistoryDetail = useCallback((entry) => {
    const { type, operationDetails, amount, movementType } = entry || {};
    if (!operationDetails || typeof operationDetails !== 'object') {
      if ((type || '').toLowerCase() === 'weekly_refresh' || (movementType || '').toLowerCase() === 'credit') {
        return t('aiBattery.history.detail.weeklyRefresh', {
          defaultValue: 'Refill settimanale'
        });
      }
      return null;
    }

    const normalizedType = (type || '').toLowerCase();

    if (
      normalizedType === 'strategy_generation' ||
      normalizedType === 'strategy_week_regeneration' ||
      normalizedType === 'post_generation'
    ) {
      const model = operationDetails.model_used || operationDetails.model || '';
      const totalTokens = operationDetails.total_tokens
        ?? (toNumber(operationDetails.input_tokens, 0) + toNumber(operationDetails.output_tokens, 0));

      const tokensLabel = formatTokens(totalTokens);
      if (model && tokensLabel) {
        return t('aiBattery.history.detail.modelWithTokens', { model, tokens: tokensLabel });
      }
      return model || tokensLabel;
    }

    if (
      normalizedType === 'image_generation' ||
      normalizedType === 'campaign_image_generation' ||
      normalizedType === 'enhanced_image_generation'
    ) {
      const count = operationDetails.image_count
        ?? operationDetails.generated_count
        ?? operationDetails.total_images
        ?? (Array.isArray(operationDetails.images) ? operationDetails.images.length : null);
      const ratio = operationDetails.ratio || operationDetails.image_ratio;

      const parts = [];
      const countLabel = formatImages(count);
      if (countLabel) {
        parts.push(countLabel);
      }
      if (ratio) {
        parts.push(t('aiBattery.history.detail.ratio', { ratio }));
      }

      return parts.join(' - ') || null;
    }

    if (normalizedType === 'analysis') {
      const analyst = (operationDetails.operation && operationDetails.operation.trim().length > 0)
        ? operationDetails.operation
        : t('aiBattery.history.detail.analysisDefault');

      const tokensLabel = formatTokens(operationDetails.total_tokens ?? operationDetails.input_tokens);
      if (tokensLabel) {
        return t('aiBattery.history.detail.analysisWithTokens', {
          analyst,
          tokens: tokensLabel
        });
      }
      return analyst;
    }

    if (normalizedType === 'credit_addition') {
      return operationDetails.reason || t('aiBattery.history.detail.creditAddition');
    }

    if (normalizedType === 'weekly_refresh') {
      const creditAmount = toNumber(amount, NaN);
      const formattedAmount = Number.isFinite(creditAmount)
        ? decimalFormatter.format(creditAmount)
        : null;

      const planCode = (operationDetails.plan_code || '').toString();
      const planName = operationDetails.plan_name || planCode;

      if (formattedAmount && planName) {
        return t('aiBattery.history.detail.weeklyRefreshPlanAmount', {
          plan: planName,
          amount: formattedAmount,
          defaultValue: `Refill ${planName} (+${formattedAmount})`
        });
      }

      if (formattedAmount) {
        return t('aiBattery.history.detail.weeklyRefreshAmount', {
          amount: formattedAmount,
          defaultValue: `Refill settimanale (+${formattedAmount})`
        });
      }

      return t('aiBattery.history.detail.weeklyRefresh', {
        defaultValue: 'Refill settimanale'
      });
    }

    return null;
  }, [decimalFormatter, formatImages, formatTokens, t]);

  const setDefaultBatteryData = useCallback(() => {
    const now = Date.now();
    const weeklyQuota = 1.5;
    const remainingCredits = 0.9;
    const consumedCredits = weeklyQuota - remainingCredits;
    const defaultCycle = computeWeeklyCycle({
      currentWeekStart: new Date(now - 3 * 24 * 60 * 60 * 1000),
      currentWeekEnd: new Date(now + 4 * 24 * 60 * 60 * 1000),
      lastRefreshAt: new Date(now - 2 * 24 * 60 * 60 * 1000)
    });

    setBatteryData({
      percentage: Math.max(0, Math.min(100, Math.round((remainingCredits / weeklyQuota) * 100))),
      capacityUnits: weeklyQuota,
      baseCapacityUnits: weeklyQuota,
      remainingUnits: remainingCredits,
      consumedUnits: consumedCredits,
      isUpgraded: false,
      lastRecharge: defaultCycle.lastRefresh,
      nextRecharge: defaultCycle.nextRefresh,
      status: 'active',
      lastUpdated: new Date(),
      weekStart: defaultCycle.weekStart,
      weekEnd: defaultCycle.weekEnd,
      subscription: null,
      weekKey: null
    });

    setUsageOverview({
      operationCount: 2,
      byType: {
        strategy_generation: { count: 1 },
        image_generation: { count: 1 }
      }
    });

    setLastUsage({
      timestamp: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
      type: 'debit',
      operation_type: 'strategy_generation',
      amount: consumedCredits / 2,
      operation_details: {
        model_used: 'gpt-5-mini',
        total_tokens: 2500
      },
      balance_after: remainingCredits
    });
  }, []);

  const setDefaultBatteryHistory = useCallback(() => {
    const now = Date.now();
    setBatteryHistory([
      {
        id: 1,
        action: getActionNameFromType('post_generation'),
        timestamp: new Date(now - 1000 * 60 * 30),
        type: 'post_generation',
        operationDetails: {
          model_used: 'GPT-5 mini',
          total_tokens: 1200
        }
      },
      {
        id: 2,
        action: getActionNameFromType('strategy_week_regeneration'),
        timestamp: new Date(now - 1000 * 60 * 60 * 2),
        type: 'strategy_week_regeneration',
        operationDetails: {
          model_used: 'GPT-5 mini',
          total_tokens: 1800
        }
      },
      {
        id: 3,
        action: getActionNameFromType('analysis'),
        timestamp: new Date(now - 1000 * 60 * 60 * 5),
        type: 'analysis',
        operationDetails: {
          total_tokens: 1400
        }
      }
    ]);
  }, [getActionNameFromType]);

  const fetchBatteryStatus = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiFetch(`${API_CONFIG.BASE_URL}/api/v1/user/ai-battery`, {
        method: 'GET'
      });

      if (!response.ok) {
        console.error('[MobileBatteryPanel] Failed to fetch battery status:', response.status);
        setDefaultBatteryData();
        return;
      }

      const data = await parseResponseBody(response);
      const apiBattery = data.ai_battery || {};
      const subscriptionInfo = apiBattery.subscription || {};

      const weeklyQuota = toNumber(apiBattery.weekly_quota, NaN);
      const remainingCredits = toNumber(apiBattery.remaining_credits, NaN);
      const subscriptionWeeklyCredits = toNumber(subscriptionInfo.weekly_credits, NaN);
      const consumedCredits = toNumber(
        apiBattery.consumed_credits,
        Number.isFinite(weeklyQuota) && Number.isFinite(remainingCredits)
          ? weeklyQuota - remainingCredits
          : NaN
      );

      const cycle = computeWeeklyCycle({
        currentWeekStart: apiBattery.current_week_start,
        currentWeekEnd: apiBattery.current_week_end,
        nextRefreshAt: subscriptionInfo.next_refresh_at || apiBattery.next_refresh_at,
        lastRefreshAt: apiBattery.last_refresh_at || subscriptionInfo.last_refresh_at
      });

      const safeWeeklyQuota = Number.isFinite(weeklyQuota) && weeklyQuota > 0
        ? weeklyQuota
        : (Number.isFinite(subscriptionWeeklyCredits) && subscriptionWeeklyCredits > 0
          ? subscriptionWeeklyCredits
          : (Number.isFinite(remainingCredits) && remainingCredits > 0 ? remainingCredits : 1));

      const safeRemaining = Number.isFinite(remainingCredits)
        ? Math.max(0, remainingCredits)
        : safeWeeklyQuota;

      const derivedConsumed = Number.isFinite(consumedCredits)
        ? Math.max(0, consumedCredits)
        : Math.max(0, safeWeeklyQuota - safeRemaining);

      const consumedPercentage = toNumber(apiBattery.consumed_percentage, NaN);
      const percentageRemaining = safeWeeklyQuota > 0
        ? Math.max(0, Math.min(100, Math.round((safeRemaining / safeWeeklyQuota) * 100)))
        : (Number.isFinite(consumedPercentage) ? Math.max(0, Math.min(100, Math.round(100 - consumedPercentage))) : 0);

      const planCode = (subscriptionInfo.plan_code || '').toString().toLowerCase();
      const isUpgraded = safeWeeklyQuota > 1 || (planCode && planCode !== 'free_trial');

      setBatteryData({
        percentage: percentageRemaining,
        capacityUnits: safeWeeklyQuota,
        baseCapacityUnits: Number.isFinite(subscriptionWeeklyCredits)
          ? subscriptionWeeklyCredits
          : safeWeeklyQuota,
        remainingUnits: safeRemaining,
        consumedUnits: derivedConsumed,
        isUpgraded,
        lastRecharge: cycle.lastRefresh,
        nextRecharge: cycle.nextRefresh,
        status: apiBattery.status || (safeRemaining <= 0 ? 'depleted' : 'active'),
        lastUpdated: new Date(),
        weekStart: cycle.weekStart,
        weekEnd: cycle.weekEnd,
        subscription: subscriptionInfo,
        weekKey: apiBattery.week_key
      });

      const recentActivity = apiBattery.recent_activity || {};
      const recentTransactions = Array.isArray(recentActivity.transactions)
        ? recentActivity.transactions
        : [];
      const debitTransactions = recentTransactions.filter((tx) => (tx?.type || '').toLowerCase() === 'debit');

      const byType = debitTransactions.reduce((acc, tx) => {
        const type = tx?.operation_type || 'unknown';
        const current = acc[type] || { count: 0 };
        current.count += 1;
        acc[type] = current;
        return acc;
      }, {});

      setUsageOverview({
        operationCount: toNumber(
          recentActivity.operation_count,
          toNumber(recentActivity.transaction_count, debitTransactions.length)
        ),
        byType
      });

      const lastTransaction = recentActivity.last_transaction
        || debitTransactions[0]
        || recentTransactions[0]
        || null;
      setLastUsage(lastTransaction ? { ...lastTransaction } : null);

      console.log('[MobileBatteryPanel] Battery status updated (weekly model):', data);
    } catch (error) {
      console.error('[MobileBatteryPanel] Error fetching battery status:', error);
      setDefaultBatteryData();
    } finally {
      setIsLoading(false);
    }
  }, [setDefaultBatteryData]);

  const fetchBatteryHistory = useCallback(async () => {
    setIsHistoryLoading(true);
    try {
      const response = await apiFetch(`${API_CONFIG.BASE_URL}/api/v1/user/ai-battery/usage-history?limit=10&offset=0`, {
        method: 'GET'
      });

      if (!response.ok) {
        console.error('[MobileBatteryPanel] Failed to fetch battery history:', response.status);
        setDefaultBatteryHistory();
        return;
      }

      const data = await parseResponseBody(response);
      const transactions = Array.isArray(data.transactions) ? data.transactions : [];
      const formattedHistory = transactions.map((item, index) => {
        const timestampSource = pickTimestampSource(item) || item.created_at;
        const timestamp = parseAPITimestamp(timestampSource) || new Date();

        return {
          id: item.id || `${timestamp.getTime()}-${index}`,
          action: getActionNameFromType(item.operation_type, item),
          timestamp,
          type: item.operation_type,
          operationDetails: item.operation_details,
          amount: item.amount,
          balance_after: item.balance_after,
          movementType: item.type
        };
      });

      setBatteryHistory(formattedHistory);
      console.log('[MobileBatteryPanel] Battery history updated:', formattedHistory);
    } catch (error) {
      console.error('[MobileBatteryPanel] Error fetching battery history:', error);
      setDefaultBatteryHistory();
    } finally {
      setIsHistoryLoading(false);
    }
  }, [getActionNameFromType, setDefaultBatteryHistory]);

  useEffect(() => {
    let isMounted = true;

    const loadBatteryData = async () => {
      await Promise.all([fetchBatteryStatus(), fetchBatteryHistory()]);
    };

    loadBatteryData();

    const refreshInterval = setInterval(() => {
      if (!isMounted) return;
      fetchBatteryStatus();
      fetchBatteryHistory();
    }, 5 * 60 * 1000);

    return () => {
      isMounted = false;
      clearInterval(refreshInterval);
    };
  }, [fetchBatteryHistory, fetchBatteryStatus]);


  const batteryStatusClass = useMemo(() => {
    if (batteryData.percentage < 20) return 'low';
    if (batteryData.percentage < 50) return 'medium';
    return 'high';
  }, [batteryData.percentage]);

  const usageByType = useMemo(() => {
    const entries = usageOverview.byType || {};
    return Object.entries(entries)
      .map(([type, info]) => ({
        type,
        count: toNumber(info?.count, null)
      }))
      .sort((a, b) => (toNumber(b.count, 0) - toNumber(a.count, 0)));
  }, [usageOverview.byType]);

  const lastUsageSummary = useMemo(() => {
    if (!lastUsage) {
      return t('aiBattery.stats.lastActivity.empty');
    }

    const actionName = getActionNameFromType(lastUsage.operation_type, lastUsage);

    const resolvedTimestamp = resolveTimestamp(lastUsage);

    let timestampText = resolvedTimestamp ? formatTimestamp(resolvedTimestamp) : '--';

    if (!timestampText || timestampText === '--') {
      const fallbackTime = extractRelativeTimeLabels(lastUsage).find((value) => {
        if (typeof value !== 'string') {
          return false;
        }

        const trimmed = value.trim();
        return trimmed.length > 0 && trimmed !== '--';
      });

      if (fallbackTime) {
        timestampText = fallbackTime.trim();
      }
    }

    if (!actionName && !timestampText) {
      return t('aiBattery.stats.lastActivity.empty');
    }

    return t('aiBattery.stats.lastActivity.value', {
      action: actionName,
      time: timestampText
    });
  }, [formatTimestamp, getActionNameFromType, lastUsage, t]);

  const formattedNextRecharge = useMemo(() => (
    batteryData.nextRecharge ? formatShortDate(batteryData.nextRecharge) : '--'
  ), [batteryData.nextRecharge, formatShortDate]);

  const capacityLabel = useMemo(() => {
    if (!Number.isFinite(batteryData.capacityUnits)) {
      return '--';
    }

    const formattedQuota = decimalFormatter.format(batteryData.capacityUnits);
    const planName = batteryData.subscription?.plan_name
      || batteryData.subscription?.plan_code
      || null;

    if (planName) {
      return t('aiBattery.stats.capacity.plan', {
        plan: planName,
        amount: formattedQuota,
        defaultValue: `${planName} - ${formattedQuota}`
      });
    }

    if (!batteryData.baseCapacityUnits) {
      return formattedQuota;
    }

    const multiplier = batteryData.capacityUnits / batteryData.baseCapacityUnits;
    if (multiplier <= 1.01) {
      return t('aiBattery.stats.capacity.standard', {
        amount: formattedQuota,
        defaultValue: formattedQuota
      });
    }

    const formattedMultiplier = decimalFormatter.format(multiplier);
    return t('aiBattery.stats.capacity.upgraded', {
      multiplier: formattedMultiplier,
      amount: formattedQuota,
      defaultValue: `${formattedMultiplier}x - ${formattedQuota}`
    });
  }, [batteryData.baseCapacityUnits, batteryData.capacityUnits, batteryData.subscription, decimalFormatter, t]);

  const handleCapacityCardClick = useCallback(() => {
    if (typeof onOpenPlans === 'function') {
      onOpenPlans();
    }
  }, [onOpenPlans]);

  const handleCapacityCardKeyDown = useCallback((event) => {
    if (!onOpenPlans) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpenPlans();
    }
  }, [onOpenPlans]);

  if (isLoading) {
    return renderLoadingSkeleton();
  }

  return (
    <div className="mobile-battery-panel">
      <div className="mobile-battery-content">
        <div className="mobile-battery-header">
          <h1 className="mobile-battery-title">{t('aiBattery.header.title')}</h1>
          <p className="mobile-battery-subtitle">
            {t('aiBattery.header.subtitle')}
          </p>
        </div>

        <div className="mobile-battery-display-section">
          <div className={`mobile-battery-bar ${batteryStatusClass}`}>
            <div
              className="mobile-battery-fill"
              style={{ '--battery-percentage': `${batteryData.percentage}%` }}
            >
              <Iridescence
                className="iridescence-overlay"
                color={[0.706, 0.477, 0.683]}
                mouseReact={false}
                amplitude={0.08}
                speed={1.0}
              />
            </div>
            <div className="mobile-battery-percentage-text">
              {batteryData.percentage}%
            </div>
          </div>
        </div>

        <div className="mobile-battery-stats">
          <div
            className={`mobile-battery-stat-card ${onOpenPlans ? 'is-action' : ''}`}
            role={onOpenPlans ? 'button' : undefined}
            tabIndex={onOpenPlans ? 0 : undefined}
            onClick={onOpenPlans ? handleCapacityCardClick : undefined}
            onKeyDown={onOpenPlans ? handleCapacityCardKeyDown : undefined}
          >
            <span className="mobile-battery-stat-label">{t('aiBattery.stats.capacity.label')}</span>
            <span className="mobile-battery-stat-value">{capacityLabel}</span>
            <span className="mobile-battery-stat-help">{t('aiBattery.stats.capacity.help')}</span>
          </div>
          <div className="mobile-battery-stat-card">
            <span className="mobile-battery-stat-label">{t('aiBattery.stats.nextRecharge.label')}</span>
            <span className="mobile-battery-stat-value">{formattedNextRecharge}</span>
            <span className="mobile-battery-stat-help">{t('aiBattery.stats.nextRecharge.help')}</span>
          </div>
          <div className="mobile-battery-stat-card">
            <span className="mobile-battery-stat-label">{t('aiBattery.stats.operations.label')}</span>
            <span className="mobile-battery-stat-value">{formatCount(usageOverview.operationCount)}</span>
            <span className="mobile-battery-stat-help">{t('aiBattery.stats.operations.help')}</span>
          </div>
          <div className="mobile-battery-stat-card">
            <span className="mobile-battery-stat-label">{t('aiBattery.stats.lastActivity.label')}</span>
            <span className="mobile-battery-stat-value mobile-battery-stat-value-small">{lastUsageSummary}</span>
          </div>
        </div>

        {usageByType.length > 0 && (
          <div className="mobile-usage-breakdown">
            <div className="mobile-usage-breakdown-header">
              <img src={iconAi} alt={t('aiBattery.alt.ai')} className="mobile-usage-icon" />
              <div className="mobile-usage-text">
                <h3 className="mobile-section-title">{t('aiBattery.usage.title')}</h3>
                <p className="mobile-explanation-subtitle">
                  {t('aiBattery.usage.subtitle')}
                </p>
              </div>
            </div>
            <div className="mobile-usage-chips">
              {usageByType.map((item) => (
                <div key={item.type} className="mobile-usage-chip">
                  <span className="mobile-usage-chip-title">{getActionNameFromType(item.type)}</span>
                  <span className="mobile-usage-chip-meta">
                    {formatOperationsLabel(item.count)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mobile-battery-history">
          <h3 className="mobile-section-title">{t('aiBattery.history.title')}</h3>
          <div className="mobile-history-list">
            {isHistoryLoading && batteryHistory.length === 0 ? (
              renderHistorySkeleton()
            ) : (
              batteryHistory.map((item) => {
                const detail = computeHistoryDetail(item);
                return (
                  <div key={item.id} className="mobile-history-item">
                    <div className="mobile-history-icon">
                      <img src={iconAi} alt={t('aiBattery.alt.ai')} />
                    </div>
                    <div className="mobile-history-content">
                      <div className="mobile-history-header">
                        <span className="mobile-history-action">{item.action}</span>
                      </div>
                      <div className="mobile-history-meta">
                        <div className="mobile-history-timestamp">
                          <img src={iconCalendar} alt={t('aiBattery.alt.calendar')} className="mobile-time-icon" />
                          {formatTimestamp(item.timestamp)}
                        </div>
                        {detail && (
                          <div className="mobile-history-detail">{detail}</div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileBatteryPanel;
